"use client";

import { useSeo } from "../context/SeoContext";
import { useGlobalContent } from "../context/GlobalContentProvider";
import { ISeoContent, IStructuredData } from "@/shared/models/content/seo-content";
import { mergeSeoData, generateWellnessStructuredData } from "../services/seoService";

/**
 * Hook to get current SEO data and update it for specific pages
 * NOTE: For initial page structured data, prefer server-side generation with StructuredDataSSR
 * This hook is primarily for dynamic SEO updates after user interactions
 */
export const useSeoMetadata = () => {
  const { seo, updateSeo, resetToGlobalSeo } = useSeo();
  const { globalContent } = useGlobalContent();

  /**
   * Update page-specific SEO (client-side only)
   * For initial page SEO, use generateMetadata() in page components instead
   */
  const setPageSeo = (pageSeo: ISeoContent) => {
    updateSeo(pageSeo);
  };

  /**
   * Reset to global SEO
   */
  const resetSeo = () => {
    resetToGlobalSeo();
  };

  /**
   * Get merged SEO with global and page-specific settings
   */
  const getMergedSeo = (pageSeo: ISeoContent): ISeoContent => {
    return mergeSeoData(globalContent.seo, pageSeo);
  };

  /**
   * Generate default structured data for wellness website
   * NOTE: This is async and mainly for client-side dynamic updates
   * For initial page load, use server-side generation instead
   */
  const getWellnessStructuredData = async (): Promise<IStructuredData[]> => {
    return await generateWellnessStructuredData();
  };

  /**
   * Set page SEO with structured data (for dynamic updates only)
   * For initial page load, use server-side generation with StructuredDataSSR
   */
  const setPageSeoWithStructuredData = async (pageSeo: ISeoContent) => {
    try {
      // Make a deep copy to avoid modifying the original
      const pageSeoWithData = { ...pageSeo };

      // Ensure we have structured data
      if (!pageSeoWithData.structured_data) {
        pageSeoWithData.structured_data = await getWellnessStructuredData();
      }

      updateSeo(pageSeoWithData);
    } catch (error) {
      console.error("Error setting page SEO with structured data:", error);
      // Still update with what we have
      updateSeo(pageSeo);
    }
  };

  return {
    seo,
    globalSeo: globalContent.seo,
    setPageSeo,
    resetSeo,
    getMergedSeo,
    getWellnessStructuredData,
    setPageSeoWithStructuredData
  };
};
